import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarClock,
  Info,
  PencilLine,
  Plus,
} from 'lucide-react'

import { ApiError } from '../../services/api/client'
import { Button } from '../../ui/Button/Button'
import { Switch } from '../../ui/Switch/Switch'
import { useScopedToast } from '../../ui/Toaster/useScopedToast'
import {
  ageGroupLabel,
  canonicalAgePolicy,
  clinicDaySpan,
  editsByFirstAge,
  extendToEveryAge,
  lowerFirst,
  missingAgesSummary,
  payloadFromGroups,
  showsClosedHours,
  sortedGroups,
  timelineRange,
  windowsLabel,
  type AgeGroupDraft,
} from './age-scheduling-model'
import { AgeGroupDialog, type AgeGroupDialogTarget } from './AgeGroupDialog'
import { AgeTimeline, AgeTimelineAxis } from './AgeTimeline'
import type { ProviderResourceSettings } from './provider-resource-contracts'
import type { ProviderResourceRepository } from './provider-resource-repository'
import {
  AGE_SCHEDULING_MAX_RULES,
  ageSchedulingPolicySchema,
  type AgeSchedulingPolicy,
} from './settings-contracts'
import { SettingsSaveFooter } from './SettingsSaveFooter'
import { useReportUnsavedChanges } from './settings-unsaved-changes'
import panel from './SettingsSection.module.css'
import styles from './AgeSchedulingSettings.module.css'

type ActivePolicy = NonNullable<
  NonNullable<
    ProviderResourceSettings['providerResources']
  >['flioSchedulingPolicy']
>
type PolicyDraft = { enabled: boolean; groups: AgeGroupDraft[] }

const OFF: AgeSchedulingPolicy = { enabled: false, rules: [] }

export function AgeSchedulingPolicySettings({
  tenantId,
  activePolicy,
  repository,
}: {
  tenantId: string
  activePolicy: ActivePolicy | null
  repository?: ProviderResourceRepository
}) {
  const queryClient = useQueryClient()
  const notify = useScopedToast()
  const activeTenant = useRef(tenantId)
  activeTenant.current = tenantId
  const nextKey = useRef(0)
  const stored = activePolicy?.ageSchedulingPolicy ?? OFF
  const storedKey = JSON.stringify(canonicalAgePolicy(stored))
  // The stored section this draft was built from; edits survive refetches.
  const applied = useRef(`${tenantId}|${storedKey}`)
  const [draft, setDraft] = useState<PolicyDraft>(() => toDraft(stored))
  const [error, setError] = useState<string | null>(null)
  const [dialog, setDialog] = useState<AgeGroupDialogTarget | null>(null)

  function key() {
    nextKey.current += 1
    return `age-${nextKey.current}`
  }

  function toDraft(policy: AgeSchedulingPolicy): PolicyDraft {
    return {
      enabled: policy.enabled,
      groups: policy.rules.map((rule) => ({
        key: key(),
        minimumYears: rule.minimumYears,
        ...(rule.maximumYears === undefined
          ? {}
          : { maximumYears: rule.maximumYears }),
        windows: rule.windows.map((window) => ({ key: key(), ...window })),
      })),
    }
  }

  useEffect(() => {
    const marker = `${tenantId}|${storedKey}`
    if (applied.current === marker) return
    applied.current = marker
    setDraft(toDraft(JSON.parse(storedKey) as AgeSchedulingPolicy))
    setDialog(null)
    setError(null)
    // toDraft only allocates row keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedKey, tenantId])

  const payload = useMemo(
    () => payloadFromGroups(draft.enabled, draft.groups),
    [draft]
  )
  const validation = ageSchedulingPolicySchema.safeParse(payload)
  const isDirty = JSON.stringify(payload) !== storedKey
  const editable =
    activePolicy !== null && Boolean(repository?.updateAgeSchedulingPolicy)

  const save = useMutation<
    ProviderResourceSettings,
    Error,
    { requestTenantId: string; policy: AgeSchedulingPolicy; revision: number }
  >({
    mutationFn: ({ requestTenantId, policy, revision }) =>
      repository!.updateAgeSchedulingPolicy!(requestTenantId, revision, policy),
    onSuccess: (result, variables) => {
      // Working Hours shares this policy revision; refresh every reader.
      queryClient.setQueryData(
        ['settings', variables.requestTenantId, 'provider-resources'],
        result
      )
      void queryClient.invalidateQueries({
        queryKey: ['settings', variables.requestTenantId, 'provider-resources'],
      })
      if (activeTenant.current !== variables.requestTenantId) return
      setError(null)
      notify('success', 'Age-based scheduling saved')
    },
    onError: (failure, variables) => {
      if (activeTenant.current !== variables.requestTenantId) return
      setError(
        failure instanceof ApiError && failure.status === 409
          ? 'These settings changed elsewhere. Refresh the page and try again.'
          : 'Age-based scheduling could not be saved. Your changes are still here.'
      )
    },
  })

  function submit() {
    if (!activePolicy || !editable || !validation.success) return
    if (!isDirty || save.isPending) return
    save.mutate({
      requestTenantId: tenantId,
      policy: validation.data,
      revision: activePolicy.policyRevision,
    })
  }

  const issues = validation.success
    ? []
    : [...new Set(validation.error.issues.map((issue) => issue.message))]

  useReportUnsavedChanges('appointments:age', isDirty)
  const locked = !editable || save.isPending
  const clinic = clinicDaySpan(activePolicy?.workingHours)
  const groups = sortedGroups(draft.groups)
  const byFirstAge = editsByFirstAge(draft.groups)
  const range = timelineRange(
    clinic,
    draft.groups.flatMap((group) => group.windows)
  )
  const alone = groups.length === 1

  function setEnabled(enabled: boolean) {
    setDraft((current) =>
      enabled && current.groups.length === 0
        ? {
            // One group for every age, bookable through the clinic's day.
            enabled,
            groups: [
              {
                key: key(),
                minimumYears: 0,
                windows: [
                  {
                    key: key(),
                    start: clinic?.start ?? '08:00',
                    end: clinic?.end ?? '17:00',
                  },
                ],
              },
            ],
          }
        : { ...current, enabled }
    )
  }

  return (
    <section className={panel.panel} aria-labelledby="age-scheduling-title">
      <header className={panel.panelHeader}>
        <div className={panel.heading}>
          <span className={panel.eyebrow}>Booking rules</span>
          <h2 id="age-scheduling-title">Age-based scheduling</h2>
          <p>Choose when Flio can book each age group on calls and texts.</p>
        </div>
        {activePolicy !== null ? (
          <label className={styles.policySwitch}>
            <Switch
              aria-label="Use age-based scheduling"
              checked={draft.enabled}
              disabled={locked}
              onChange={(event) => setEnabled(event.currentTarget.checked)}
            />
            <span>{draft.enabled ? 'On' : 'Off'}</span>
          </label>
        ) : null}
      </header>

      {activePolicy === null ? (
        <div className={styles.body}>
          <p className={styles.empty}>
            Set up Working Hours before configuring age-based scheduling.
          </p>
        </div>
      ) : (
        <>
          <div className={styles.body}>
            {!byFirstAge ? (
              <div className={styles.coverage} data-tone="warning">
                <AlertTriangle size={16} aria-hidden="true" />
                <div>
                  <strong>{missingAgesSummary(draft.groups)}</strong>
                  <p>
                    Every age needs a group before age-based scheduling can be
                    on. Each group can grow to fill the gaps.
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={locked}
                    onClick={() =>
                      setDraft((current) => ({
                        ...current,
                        groups: extendToEveryAge(current.groups),
                      }))
                    }
                  >
                    Fill the gaps
                  </Button>
                </div>
              </div>
            ) : null}

            {groups.length ? (
              <>
                {!draft.enabled ? (
                  <p className={styles.offNote}>
                    <Info size={15} aria-hidden="true" />
                    Off. Flio offers every age the same times; these groups are
                    kept for when you turn it on.
                  </p>
                ) : null}
                <div
                  className={styles.schedule}
                  data-off={draft.enabled ? undefined : ''}
                >
                  <div className={styles.scheduleHeader} aria-hidden="true">
                    <span>Age group</span>
                    <AgeTimelineAxis range={range} />
                    <span />
                  </div>
                  <ul className={styles.groups} aria-label="Age groups">
                    {groups.map((group) => {
                      const label = ageGroupLabel(group, { alone })
                      return (
                        <li key={group.key} className={styles.group}>
                          <div className={styles.groupText}>
                            <strong>{label}</strong>
                            <span>{windowsLabel(group.windows)}</span>
                          </div>
                          <AgeTimeline
                            windows={group.windows}
                            range={range}
                            clinic={clinic}
                            muted={!draft.enabled}
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            className={styles.editButton}
                            disabled={locked}
                            aria-label={`Edit ${lowerFirst(label)}`}
                            onClick={() =>
                              setDialog({ kind: 'edit', key: group.key })
                            }
                          >
                            <PencilLine size={15} aria-hidden="true" /> Edit
                          </Button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
                <div className={styles.scheduleFooter}>
                  {byFirstAge ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className={styles.addButton}
                      disabled={
                        locked ||
                        draft.groups.length >= AGE_SCHEDULING_MAX_RULES
                      }
                      onClick={() => setDialog({ kind: 'add' })}
                    >
                      <Plus size={16} aria-hidden="true" /> Add age group
                    </Button>
                  ) : (
                    <span />
                  )}
                  {draft.enabled && showsClosedHours(range, clinic) ? (
                    <p className={styles.legend} aria-hidden="true">
                      <span>
                        <i data-swatch="bookable" /> Bookable
                      </span>
                      <span>
                        <i data-swatch="closed" /> Clinic closed
                      </span>
                    </p>
                  ) : null}
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <span className={styles.emptyIcon} aria-hidden="true">
                  <CalendarClock size={20} />
                </span>
                <h3>Every age has the same times</h3>
                <p>
                  Give younger or older patients their own bookable times, such
                  as mornings for young children.
                </p>
                <Button
                  type="button"
                  disabled={locked}
                  onClick={() => setEnabled(true)}
                >
                  Set up age groups
                </Button>
              </div>
            )}

            {isDirty && issues.length ? (
              <div className={styles.issues} role="alert">
                {issues.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </div>
            ) : null}
          </div>

          <SettingsSaveFooter
            dirty={isDirty}
            saving={save.isPending}
            saved={save.isSuccess && !isDirty}
            error={error && isDirty ? error : null}
            problem={
              isDirty && !validation.success
                ? 'fix the age groups above to save'
                : null
            }
            canSave={editable && validation.success}
            saveLabel="Save age-based scheduling"
            onDiscard={() => {
              setDraft(toDraft(stored))
              setError(null)
            }}
            onSave={submit}
          />
        </>
      )}

      {dialog ? (
        <AgeGroupDialog
          target={dialog}
          groups={draft.groups}
          enabled={draft.enabled}
          clinic={clinic}
          newKey={key}
          onApply={(next) => {
            setDraft((current) => ({ ...current, groups: next }))
            setDialog(null)
          }}
          onCancel={() => setDialog(null)}
        />
      ) : null}
    </section>
  )
}
